import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { UsageUpdatedMessage } from '../../generated/models';
import { SubscriptionPlanChangedMessage } from '../../generated/models';
import { LlmStreamChunkMessage } from '../../generated/models';
import { LlmStreamCompletedMessage } from '../../generated/models';
import { LlmStreamFailedMessage } from '../../generated/models';
import { AgentResult } from '../../generated/models';
import { GetToolResult } from '../../generated/models';
import { CreateResult } from '../../generated/models';
import { TestDto } from '../../generated/models';
import { TestRunSummary } from '../../generated/models';
import { AgentSessionDto } from '../../generated/models';
import { ConversationDto } from '../../generated/models';
import { BenchmarkRunUpdatedMessage } from '../../generated/models';
import { BenchmarkRunItemUpdatedMessage } from '../../generated/models';

@Injectable({
  providedIn: 'root'
})
export class NotificationsHub {
  private connection: HubConnection | null = null;
  private isConnecting = false;
  private onUsageUpdatedCallback: ((data: UsageUpdatedMessage) => void) | null = null;
  private onSubscriptionPlanChangedCallback: ((data: SubscriptionPlanChangedMessage) => void) | null = null;
  private onLlmStreamChunkCallback: ((data: LlmStreamChunkMessage) => void) | null = null;
  private onLlmStreamCompletedCallback: ((data: LlmStreamCompletedMessage) => void) | null = null;
  private onLlmStreamFailedCallback: ((data: LlmStreamFailedMessage) => void) | null = null;
  private onAgentUpdatedCallback: ((data: AgentResult) => void) | null = null;
  private onToolUpdatedCallback: ((data: GetToolResult) => void) | null = null;
  private onAgentCreatedCallback: ((data: CreateResult) => void) | null = null;
  private onToolCreatedCallback: ((data: CreateResult) => void) | null = null;
  private onTestCreatedCallback: ((data: CreateResult) => void) | null = null;
  private onTestUpdatedCallback: ((data: TestDto) => void) | null = null;
  private onTestRunUpdatedCallback: ((data: TestRunSummary) => void) | null = null;
  private onAgentSessionUpdatedCallback: ((data: AgentSessionDto) => void) | null = null;
  private onConversationUpdatedCallback: ((data: ConversationDto) => void) | null = null;
  private onBenchmarkRunUpdatedCallback: ((data: BenchmarkRunUpdatedMessage) => void) | null = null;
  private onBenchmarkRunItemUpdatedCallback: ((data: BenchmarkRunItemUpdatedMessage) => void) | null = null;

  /**
   * Connect to the notifications SignalR hub
   * @param token Token used for authentication (passed as Bearer via accessTokenFactory)
   */
  async connect(token: string): Promise<void> {
    // Always abandon any prior connection — its token might be stale, and
    // even if state === Connected we don't want to silently reuse it for a
    // different session. Fire-and-forget stop(); the WS close frame is
    // sent synchronously, ack waiting is what hangs us in Reconnecting.
    if (this.connection) {
      const old = this.connection;
      this.connection = null;
      old.stop().catch(() => {});
    }

    this.isConnecting = true;

    try {
      const hubUrl = `${environment.generatedApiUrl}/hubs/notifications`;

      this.connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token
        })
        .withAutomaticReconnect()
        .build();

      this.registerEventHandlers();
      await this.connection.start();
      console.log('✅ Connected to notifications hub');
    } catch (error) {
      console.error('❌ Failed to connect to notifications hub:', error);
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  private registerEventHandlers(): void {
    if (!this.connection) return;
    if (this.onUsageUpdatedCallback) {
      this.connection.on('UsageUpdated', this.onUsageUpdatedCallback);
    }
    if (this.onSubscriptionPlanChangedCallback) {
      this.connection.on('SubscriptionPlanChanged', this.onSubscriptionPlanChangedCallback);
    }
    if (this.onLlmStreamChunkCallback) {
      this.connection.on('LlmStreamChunk', this.onLlmStreamChunkCallback);
    }
    if (this.onLlmStreamCompletedCallback) {
      this.connection.on('LlmStreamCompleted', this.onLlmStreamCompletedCallback);
    }
    if (this.onLlmStreamFailedCallback) {
      this.connection.on('LlmStreamFailed', this.onLlmStreamFailedCallback);
    }
    if (this.onAgentUpdatedCallback) {
      this.connection.on('AgentUpdated', this.onAgentUpdatedCallback);
    }
    if (this.onToolUpdatedCallback) {
      this.connection.on('ToolUpdated', this.onToolUpdatedCallback);
    }
    if (this.onAgentCreatedCallback) {
      this.connection.on('AgentCreated', this.onAgentCreatedCallback);
    }
    if (this.onToolCreatedCallback) {
      this.connection.on('ToolCreated', this.onToolCreatedCallback);
    }
    if (this.onTestCreatedCallback) {
      this.connection.on('TestCreated', this.onTestCreatedCallback);
    }
    if (this.onTestUpdatedCallback) {
      this.connection.on('TestUpdated', this.onTestUpdatedCallback);
    }
    if (this.onTestRunUpdatedCallback) {
      this.connection.on('TestRunUpdated', this.onTestRunUpdatedCallback);
    }
    if (this.onAgentSessionUpdatedCallback) {
      this.connection.on('AgentSessionUpdated', this.onAgentSessionUpdatedCallback);
    }
    if (this.onConversationUpdatedCallback) {
      this.connection.on('ConversationUpdated', this.onConversationUpdatedCallback);
    }
    if (this.onBenchmarkRunUpdatedCallback) {
      this.connection.on('BenchmarkRunUpdated', this.onBenchmarkRunUpdatedCallback);
    }
    if (this.onBenchmarkRunItemUpdatedCallback) {
      this.connection.on('BenchmarkRunItemUpdated', this.onBenchmarkRunItemUpdatedCallback);
    }
  }

  /**
   * Disconnect from the notifications SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      console.log('🔌 Disconnected from notifications hub');
    }
  }

  /**
   * Get the current connection state
   */
  get connectionState(): HubConnectionState | null {
    return this.connection?.state || null;
  }

  /**
   * Subscribe to UsageUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'UsageUpdated' event delivered via /hubs/notifications.
   */
  onUsageUpdated(callback: (data: UsageUpdatedMessage) => void): void {
    this.onUsageUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('UsageUpdated', callback);
    }
  }

  /**
   * Subscribe to SubscriptionPlanChanged events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'SubscriptionPlanChanged' event delivered via /hubs/notifications.
   */
  onSubscriptionPlanChanged(callback: (data: SubscriptionPlanChangedMessage) => void): void {
    this.onSubscriptionPlanChangedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('SubscriptionPlanChanged', callback);
    }
  }

  /**
   * Subscribe to LlmStreamChunk events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'LlmStreamChunk' event delivered via /hubs/notifications.
   */
  onLlmStreamChunk(callback: (data: LlmStreamChunkMessage) => void): void {
    this.onLlmStreamChunkCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('LlmStreamChunk', callback);
    }
  }

  /**
   * Subscribe to LlmStreamCompleted events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'LlmStreamCompleted' event delivered via /hubs/notifications.
   */
  onLlmStreamCompleted(callback: (data: LlmStreamCompletedMessage) => void): void {
    this.onLlmStreamCompletedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('LlmStreamCompleted', callback);
    }
  }

  /**
   * Subscribe to LlmStreamFailed events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'LlmStreamFailed' event delivered via /hubs/notifications.
   */
  onLlmStreamFailed(callback: (data: LlmStreamFailedMessage) => void): void {
    this.onLlmStreamFailedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('LlmStreamFailed', callback);
    }
  }

  /**
   * Subscribe to AgentUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'AgentUpdated' event delivered via /hubs/notifications.
   */
  onAgentUpdated(callback: (data: AgentResult) => void): void {
    this.onAgentUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('AgentUpdated', callback);
    }
  }

  /**
   * Subscribe to ToolUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'ToolUpdated' event delivered via /hubs/notifications.
   */
  onToolUpdated(callback: (data: GetToolResult) => void): void {
    this.onToolUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('ToolUpdated', callback);
    }
  }

  /**
   * Subscribe to AgentCreated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'AgentCreated' event delivered via /hubs/notifications.
   */
  onAgentCreated(callback: (data: CreateResult) => void): void {
    this.onAgentCreatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('AgentCreated', callback);
    }
  }

  /**
   * Subscribe to ToolCreated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'ToolCreated' event delivered via /hubs/notifications.
   */
  onToolCreated(callback: (data: CreateResult) => void): void {
    this.onToolCreatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('ToolCreated', callback);
    }
  }

  /**
   * Subscribe to TestCreated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'TestCreated' event delivered via /hubs/notifications.
   */
  onTestCreated(callback: (data: CreateResult) => void): void {
    this.onTestCreatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('TestCreated', callback);
    }
  }

  /**
   * Subscribe to TestUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'TestUpdated' event delivered via /hubs/notifications.
   */
  onTestUpdated(callback: (data: TestDto) => void): void {
    this.onTestUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('TestUpdated', callback);
    }
  }

  /**
   * Subscribe to TestRunUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'TestRunUpdated' event delivered via /hubs/notifications.
   */
  onTestRunUpdated(callback: (data: TestRunSummary) => void): void {
    this.onTestRunUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('TestRunUpdated', callback);
    }
  }

  /**
   * Subscribe to AgentSessionUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'AgentSessionUpdated' event delivered via /hubs/notifications.
   */
  onAgentSessionUpdated(callback: (data: AgentSessionDto) => void): void {
    this.onAgentSessionUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('AgentSessionUpdated', callback);
    }
  }

  /**
   * Subscribe to ConversationUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'ConversationUpdated' event delivered via /hubs/notifications.
   */
  onConversationUpdated(callback: (data: ConversationDto) => void): void {
    this.onConversationUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('ConversationUpdated', callback);
    }
  }

  /**
   * Subscribe to BenchmarkRunUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'BenchmarkRunUpdated' event delivered via /hubs/notifications.
   */
  onBenchmarkRunUpdated(callback: (data: BenchmarkRunUpdatedMessage) => void): void {
    this.onBenchmarkRunUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('BenchmarkRunUpdated', callback);
    }
  }

  /**
   * Subscribe to BenchmarkRunItemUpdated events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'BenchmarkRunItemUpdated' event delivered via /hubs/notifications.
   */
  onBenchmarkRunItemUpdated(callback: (data: BenchmarkRunItemUpdatedMessage) => void): void {
    this.onBenchmarkRunItemUpdatedCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('BenchmarkRunItemUpdated', callback);
    }
  }
}
